import { Fragment, useCallback, useRef } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import ScheduleDateStep from 'components/schedule/ScheduleDateStep';
import ScheduleCGUStep from 'components/schedule/ScheduleCGUStep';
import { FormInstance } from 'antd/lib/form/hooks/useForm';
import { ScheduleType } from 'components/schedule/ScheduleType';
import { MEETING_TYPES } from 'commons/types/meetingsTypes';
import { User } from 'stores/user/userTypes';
import { useAppDispatch } from 'stores/hooks';
import { createMeetingAsync } from 'stores/meetings/meetingsSlice';
import { Button, Form } from 'antd';
import GoogleMap from './GoogleMap';

const SCHEDULE_STEPS = {
  date_choice: 'date',
  cgu: 'cgu',
};

interface ScheduleRDV2Props {
  user: User | undefined;
  form: FormInstance<any>;
  defaultScheduledForm: ScheduleType;
  step: string;
  hideCGUs: boolean;
  onChangeStep: (nextStep: string) => () => void;
}

export default function ScheduleRDV2({
  user,
  form,
  defaultScheduledForm,
  step,
  hideCGUs,
  onChangeStep,
}: ScheduleRDV2Props) {
  const rdvContainer = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const onFinalize = useCallback(
    (schedule: ScheduleType) => {
      dispatch(
        createMeetingAsync({
          ...schedule,
          type: MEETING_TYPES.RDV_2,
          serviceID: form.getFieldValue('serviceID') || '',
        }),
      );
    },
    [dispatch, form],
  );
  const onClickScrollToRDV = useCallback(() => {
    if (rdvContainer.current) {
      rdvContainer.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <Fragment>
      <div className='flex flex-col py-8 gap-y-6'>
        <div>
          <h1 className='mb-4 text-xl font-bold text-center text-secondary'>
            Résultats de votre dépistage
          </h1>
          <div className='flex w-full mb-4 text-xs font-semibold text-white sm:text-base'>
            <div className='bg-[#4A6741] py-3 px-2 sm:px-4 flex-grow text-center rounded-l-full'>
              Risque faible
            </div>
            <div className='bg-[#FFA756] py-3 px-2 sm:px-4 flex-grow text-center'>
              Risque modéré
            </div>
            <div className='bg-[#F5761A] py-3 px-2 sm:px-4 flex-grow text-center'>
              Risque fort
            </div>
            <div className='bg-[#CE2525] py-3 px-2 sm:px-4 flex-grow text-center rounded-r-full relative'>
              Risque maximal
              <div className='absolute bottom-[-18px] w-0 h-0 border-solid border-r-8 border-l-8 border-l-transparent border-r-transparent border-b-[12px] border-b-[#565656] border-t-0' />
            </div>
          </div>
          <div className='flex justify-end w-full mb-2'>
            <div className='border-2 border-[#565656] py-3 px-2 shadow-xl'>
              <ul className='ml-5 text-sm italic font-semibold list-disc !mb-0'>
                <li>Apnée du sommeil sévère probable</li>
                <li>Risque d’AVC x3</li>
                <li>Diagnostic médical requis</li>
              </ul>
            </div>
          </div>
        </div>
        <div>
          <h1 className='mb-4 text-xl font-bold text-center text-secondary'>
            Pourquoi se faire diagnostiquer ?
          </h1>
          <div className='flex flex-col px-4 mb-6 gap-y-4'>
            <div className='flex items-center'>
              <div className='flex flex-col items-center justify-center flex-shrink-0 w-6 h-6 mr-4 overflow-hidden text-base font-extrabold text-white bg-blue-300 rounded-full'>
                <CheckOutlined />
              </div>
              <div>100% remboursé. Tiers-payant. CMU/ALD acceptés</div>
            </div>
            <div className='flex items-center'>
              <div className='flex flex-col items-center justify-center flex-shrink-0 w-6 h-6 mr-4 overflow-hidden text-base font-extrabold text-white bg-blue-300 rounded-full'>
                <CheckOutlined />
              </div>
              <div>Médecins spécialistes du sommeil</div>
            </div>
            <div className='flex items-center'>
              <div className='flex flex-col items-center justify-center flex-shrink-0 w-6 h-6 mr-4 overflow-hidden text-base font-extrabold text-white bg-blue-300 rounded-full'>
                <CheckOutlined />
              </div>
              <div>Prescription d’un traitement adapté à vos symptômes</div>
            </div>
          </div>
        </div>
      </div>
      <div
        className='pb-6'
        ref={rdvContainer}
      >
        <Form
          form={form}
          name='schedule-form'
          onFinish={onFinalize}
          initialValues={defaultScheduledForm}
          className='flex flex-col items-center'
        >
          <h1 className='mb-4 text-xl font-bold text-center text-secondary'>
            Réserver mon RDV
          </h1>
          <div
            className={
              step === SCHEDULE_STEPS.date_choice ? 'w-full' : 'hidden'
            }
          >
            <ScheduleDateStep
              patient={user?.patient}
              onNext={onChangeStep(SCHEDULE_STEPS.cgu)}
              form={form}
              nextRdvType={MEETING_TYPES.RDV_2}
              scroll
            />
          </div>
          <div className={step === SCHEDULE_STEPS.cgu ? 'w-full' : 'hidden'}>
            <Button
              onClick={onChangeStep(SCHEDULE_STEPS.date_choice)}
              type='text'
            >
              Retour
            </Button>
            <ScheduleCGUStep
              form={form}
              hideCGUs={hideCGUs}
              nextRdvType={MEETING_TYPES.RDV_2}
            />
          </div>
        </Form>
      </div>
      <div className='flex flex-col gap-y-6'>
        <div>
          <GoogleMap user={user} />
        </div>
        <div>
          <h1 className='mb-4 text-xl font-bold text-center text-secondary'>
            Quelles sont les prochaines étapes ?
          </h1>
          <div className='flex flex-col px-4 mb-6 gap-y-4'>
            <div className='flex items-center'>
              <div className='flex flex-col items-center justify-center flex-shrink-0 w-6 h-6 mr-4 overflow-hidden text-base font-extrabold text-white bg-blue-300 rounded-full'>
                <CheckOutlined />
              </div>
              <div>
                Réservez votre RDV avec l’infirmière dans les jours à venir
              </div>
            </div>
            <div className='flex items-center'>
              <div className='flex flex-col items-center justify-center flex-shrink-0 w-6 h-6 mr-4 overflow-hidden text-base font-extrabold text-white bg-blue-300 rounded-full'>
                <CheckOutlined />
              </div>
              <div>Dormez chez vous avec l’appareil de diagnostic</div>
            </div>
            <div className='flex items-center'>
              <div className='flex flex-col items-center justify-center flex-shrink-0 w-6 h-6 mr-4 overflow-hidden text-base font-extrabold text-white bg-blue-300 rounded-full'>
                <CheckOutlined />
              </div>
              <div>
                Vidéo-consultation 100% remboursée avec le médecin spécialiste
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className='grid max-w-md grid-cols-3 gap-4 mx-auto'>
            <div className='flex flex-col items-center justify-center flex-shrink-0'>
              <div className='text-4xl font-black text-secondary'>4,7</div>
              <div className='text-sm text-center sm:text-lg'>avis Google</div>
              <div className='flex items-center justify-center text-yellow-400 gap-x-1 h-[20px] sm:h-[24px] w-[104px]'>
                <Star />
                <Star />
                <Star />
                <Star />
                <Star />
              </div>
            </div>
            <div className='flex flex-col items-center justify-center flex-shrink-0'>
              <div className='text-4xl font-black text-secondary'>3000+</div>
              <div className='text-sm text-center sm:text-lg'>
                patients
                <br />
                diagnostiqués
              </div>
            </div>
            <div className='flex flex-col items-center justify-center flex-shrink-0'>
              <div className='text-4xl font-black text-secondary'>20</div>
              <div className='text-sm text-center sm:text-lg'>
                médecins
                <br />
                du sommeil
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className='grid max-w-md grid-cols-4 gap-4 mx-auto'>
            <div className='flex flex-col items-center justify-center flex-shrink-0 h-[50px]'>
              <img
                className='object-contain aspect-[120/50]'
                src='/BFM.png'
                alt='BFMTV'
              />
            </div>
            <div className='flex flex-col items-center justify-center flex-shrink-0 h-[50px]'>
              <img
                className='object-contain'
                src='/LeMonde.png'
                alt='Le Monde'
              />
            </div>
            <div className='flex flex-col items-center justify-center flex-shrink-0 h-[50px]'>
              <img
                className='object-contain'
                src='/Maddyness.png'
                alt='Maddyness'
              />
            </div>
            <div className='flex flex-col items-center justify-center flex-shrink-0 h-[50px]'>
              <img
                className='object-contain'
                src='/LeFigaro.png'
                alt='Le Figaro'
              />
            </div>
          </div>
        </div>
      </div>
      <div className='py-8'>
        <h1 className='mb-4 text-xl font-bold text-center text-secondary'>
          Présentation de l’équipe et du cabinet
        </h1>
        <div className='flex flex-col justify-center gap-8 md:flex-row'>
          <img
            className='col-span-2 h-[256px] object-contain'
            src='/cabinet.jpeg'
            alt='Cabinet'
          />
          <img
            className='h-[256px] object-contain'
            src='/nurse.jpg'
            alt='Infirmier'
          />
        </div>
        <div className='flex flex-col items-center pt-8 jusfity-center'>
          <Button
            onClick={onClickScrollToRDV}
            className='mx-auto'
            type='primary'
            size='large'
          >
            Réserver mon RDV
          </Button>
        </div>
      </div>
    </Fragment>
  );
}

function Star() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 32 32'
      width='32px'
      height='32px'
    >
      <path
        d='M31.77 11.857H19.74L15.99.5l-3.782 11.357H0l9.885 6.903-3.692 11.21 9.736-7.05 9.796 6.962-3.722-11.18 9.766-6.845z'
        fill='currentColor'
      />
    </svg>
  );
}
